import type { ConcernCriterionKind } from './api/work-item-contracts';
import { formatMoney } from './components/sec-adv-values';
import { disclosureField } from './disclosure-fields';

/**
 * A concern criterion's predicate, as the builder reads, composes and says it (SR-227, design
 * `docs/superpowers/specs/2026-10-05-concern-criteria-builder-ui.md` §2–§4).
 *
 * **Two tables here are mirrors of survey-api, and move with it** — the `COLUMN_DEFAULTS`
 * precedent. `PREDICATE_MEMBERS` mirrors `ConcernPredicate.Readable` (the nine disclosure
 * members a rule can name, each with its type) and `OPS_BY_TYPE` mirrors `OpsByType` (the
 * operators each type takes, in the parser's order), both in
 * `Veritant.Surveys.Domain/WorkItems/ConcernPredicate.cs`. The wire carries neither, and a builder
 * that offered a member or an operator the parser refuses would be offering a mistake.
 *
 * **What this module must never become** is what the roadmap warned a builder would be: "a second
 * evaluator of what survey-api already refuses in words". It knows what the parser *offers*; it
 * never decides what *holds* — no predicate is tested against a disclosure here, and nothing is
 * refused before it is sent. The rejected alternative, checking a rule client-side before Save,
 * would be a second parser that agrees with survey-api's until the day one of them moves.
 *
 * - **`readPredicate` is strict and total.** It answers the builder's tree or `null`, never throws
 *   and never coerces: an unknown key, a member outside the nine, an operator outside the member's
 *   type, a value of another shape (`"100000"` for a number, `false` for `blank`), a count that is
 *   not a whole number of at least 1, an empty group — each `null`. `null` means "the words cannot
 *   say this", never "this is invalid": the page then shows the rule as stored, and a rule the
 *   parser would refuse is therefore one the page never puts into confident words.
 * - **`writePredicate` composes, it does not refuse.** A number box's text goes as a JSON number
 *   only where it is plain decimal text once the dollar sign, grouping commas and spaces are set
 *   aside — `1e5` and `12%` go **as typed**, for survey-api to refuse at the row (the team firms'
 *   rule); an empty text as `""`, an empty list as `[]`, an unchosen field or operator as a leaf
 *   without that key. Each node's JSON path comes back beside the body, in survey-api's spelling
 *   (`predicate.all[1]`), so a problem it answers is drawn on the row it names.
 * - **`ruleWords` is the one reading in words**, for the reading view and the form's sentence
 *   alike: what a colleague reads afterwards is what the author read back while writing.
 *
 * Labels are `disclosure-fields.ts`' — Salesforce's field names, which staff know a disclosure
 * by — read by code; the operator words are this module's (the design's §3.1).
 */

/** A readable member's type — `ConcernPredicate.MemberType`. */
export type PredicateMemberType = 'date' | 'text' | 'number' | 'words';

/** An operator of `OpsByType`; a stored spelling outside it is a string the words cannot say. */
export type PredicateOp = 'eq' | 'in' | 'gte' | 'contains_any' | 'blank';

/**
 * `ConcernPredicate.Readable`, in the parser's order — which is the order the builder offers them
 * in. A `Map`, so a code spelled like an `Object.prototype` member reads as unknown.
 */
export const PREDICATE_MEMBERS: ReadonlyMap<string, PredicateMemberType> = new Map([
  ['event_date', 'date'],
  ['disclosure_type', 'text'],
  ['resolution', 'text'],
  ['damage_amount_requested', 'number'],
  ['settlement_amount', 'number'],
  ['damages_granted', 'number'],
  ['judgment_lien_amount', 'number'],
  ['allegations', 'words'],
  ['sanctions', 'words']
]);

/** `ConcernPredicate.OpsByType`, each list in the parser's order. */
export const OPS_BY_TYPE: Readonly<Record<PredicateMemberType, readonly PredicateOp[]>> = {
  date: ['eq', 'gte', 'blank'],
  text: ['eq', 'in', 'blank'],
  number: ['eq', 'gte', 'blank'],
  words: ['contains_any', 'blank']
};

/** The operators in words, per type — the design's §3.1. */
export const OP_WORDS: Readonly<Record<PredicateMemberType, Partial<Record<PredicateOp, string>>>> =
  {
    date: { eq: 'is on', gte: 'is on or after', blank: 'is blank' },
    text: { eq: 'is', in: 'is one of', blank: 'is blank' },
    number: { eq: 'is exactly', gte: 'is at least', blank: 'is blank' },
    words: { contains_any: 'mentions any of', blank: 'is blank' }
  };

/** A group's words after its all / any — the reading view's and the builder's header alike. */
export const GROUP_WORDS: Readonly<Record<'all' | 'any', string>> = {
  all: 'of these are true:',
  any: 'of these is true:'
};

/** Whether an operator takes a list — `in` and `contains_any`. */
export function isListOp(op: string): boolean {
  return op === 'in' || op === 'contains_any';
}

/**
 * One condition, `{member, op, value}`. `member` and `op` are `''` while the builder has none
 * chosen. `value` is what the controls hold: text for a date, a number box or Text `eq`; a list
 * for `in` and `contains_any`; `true` for `blank`. A number read from storage stays the number it
 * was, so a rule read and written back sends what it read.
 */
export interface IPredicateLeaf {
  id: string;
  type: 'leaf';
  member: string;
  op: string;
  value: PredicateValue;
}

export type PredicateValue = string | number | true | readonly string[];

/** A group, `{all: […]}` or `{any: […]}`, whose items are conditions or groups, to any depth. */
export interface IPredicateGroup {
  id: string;
  type: 'group';
  op: 'all' | 'any';
  items: readonly PredicateNode[];
}

export type PredicateNode = IPredicateLeaf | IPredicateGroup;

/**
 * A predicate as the builder holds it: the group, and for an aggregate the count it needs — the
 * number read from storage, or the box's text. `null` for a `disclosure` criterion.
 */
export interface IPredicateTree {
  root: IPredicateGroup;
  atLeast: Nullable<number | string>;
}

let lastId = 0;

/** A node id, unique for the page load — a React key and the key of `writePredicate`'s paths. */
function nextId(): string {
  lastId += 1;
  return `node-${lastId}`;
}

/** A blank condition — no field, no operator, no value. */
export function newLeaf(): IPredicateLeaf {
  return { id: nextId(), type: 'leaf', member: '', op: '', value: '' };
}

/** A group holding one blank condition: the parser refuses an empty one, so none is drawn. */
export function newGroup(op: 'all' | 'any' = 'all'): IPredicateGroup {
  return { id: nextId(), type: 'group', op, items: [newLeaf()] };
}

/** A member's type, or `undefined` for a code `Readable` does not hold. */
export function predicateMemberType(member: string): PredicateMemberType | undefined {
  return PREDICATE_MEMBERS.get(member);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Whether every key of `record` is one of `known` — the parser's "is not a known key". */
function onlyKeys(record: Record<string, unknown>, ...known: string[]): boolean {
  return Object.keys(record).every((key) => known.includes(key));
}

/** `System.Decimal`'s range, which `TryGetDecimal` reads a number into. */
const DECIMAL_MAX = 7.922816251426434e28;

/** `Int32`'s range, which `TryGetInt32` reads an aggregate's `gte` into. */
const INT32_MAX = 2147483647;

/** Whether `text` is a day as `DateOnly.TryParseExact(…, "yyyy-MM-dd")` takes one. */
function isDay(text: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1) return false;
  const date = new Date(Date.UTC(2000, month - 1, day));
  date.setUTCFullYear(year);
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function isNonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/** `Words` in the parser: a non-empty array of non-blank strings. */
function isWordList(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0 && value.every(isNonBlank);
}

/** Whether `value` is what `Test` takes for `(type, op)` — the shape, read as the parser reads it. */
function holdsShape(type: PredicateMemberType, op: PredicateOp, value: unknown): boolean {
  if (op === 'blank') return value === true;
  switch (type) {
    case 'date':
      return typeof value === 'string' && isDay(value);
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= DECIMAL_MAX;
    case 'text':
      return op === 'eq' ? isNonBlank(value) : isWordList(value);
    case 'words':
      return isWordList(value);
  }
}

function readLeaf(value: unknown): IPredicateLeaf | null {
  if (!isRecord(value) || !onlyKeys(value, 'member', 'op', 'value')) return null;
  const { member, op } = value;
  if (typeof member !== 'string' || typeof op !== 'string' || !('value' in value)) return null;
  const type = PREDICATE_MEMBERS.get(member);
  if (!type) return null;
  const ops: readonly string[] = OPS_BY_TYPE[type];
  if (!ops.includes(op) || !holdsShape(type, op as PredicateOp, value.value)) return null;
  const stored = value.value as PredicateValue;
  return {
    id: nextId(),
    type: 'leaf',
    member,
    op,
    value: Array.isArray(stored) ? [...stored] : stored
  };
}

/** A group, or a condition — dispatched as the parser does: an object holding `all` or `any` is a
 *  group (and then refused if it holds anything else). */
function readNode(value: unknown): PredicateNode | null {
  return isRecord(value) && ('all' in value || 'any' in value) ? readGroup(value) : readLeaf(value);
}

function readGroup(value: unknown): IPredicateGroup | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value);
  if (keys.length !== 1 || (keys[0] !== 'all' && keys[0] !== 'any')) return null;
  const op = keys[0];
  const items = value[op];
  if (!Array.isArray(items) || items.length === 0) return null;
  const nodes: PredicateNode[] = [];
  for (const item of items) {
    const node = readNode(item);
    if (!node) return null;
    nodes.push(node);
  }
  return { id: nextId(), type: 'group', op, items: nodes };
}

/**
 * A stored predicate as the builder's tree, or `null` where the words cannot say it — anything
 * outside the grammar of the design's §2, read strictly (see the module's JSDoc). A kind other
 * than `disclosure` and `aggregate` is `null` too: its shape is not this module's to guess.
 */
export function readPredicate(kind: ConcernCriterionKind, value: unknown): IPredicateTree | null {
  if (kind === 'disclosure') {
    const root = readGroup(value);
    return root ? { root, atLeast: null } : null;
  }
  if (kind !== 'aggregate') return null;
  if (!isRecord(value) || !onlyKeys(value, 'count', 'gte')) return null;
  const { count, gte } = value;
  if (typeof gte !== 'number' || !Number.isInteger(gte) || gte < 1 || gte > INT32_MAX) return null;
  const root = readGroup(count);
  return root ? { root, atLeast: gte } : null;
}

/** Plain decimal text, once the dollar sign, grouping commas and spaces are set aside —
 *  presentation, `readNumber`'s reading; `1e5`, `0x10` and `12%` are not. */
export function plainNumber(text: string): number | null {
  const bare = text.replace(/[$,\s]/g, '');
  return /^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(bare) ? Number(bare) : null;
}

function writeValue(leaf: IPredicateLeaf): unknown {
  if (leaf.op === 'blank') return true;
  const { value } = leaf;
  if (typeof value === 'string' && predicateMemberType(leaf.member) === 'number') {
    return plainNumber(value) ?? value;
  }
  return Array.isArray(value) ? [...value] : value;
}

function writeNode(node: PredicateNode, path: string, paths: Map<string, string>): unknown {
  paths.set(node.id, path);
  if (node.type === 'group') {
    return {
      [node.op]: node.items.map((item, i) => writeNode(item, `${path}.${node.op}[${i}]`, paths))
    };
  }
  return {
    ...(node.member === '' ? {} : { member: node.member }),
    ...(node.op === '' ? {} : { op: node.op }),
    value: writeValue(node)
  };
}

/** What `writePredicate` composes: the body's `predicate`, and each node's path by its id. */
export interface IWrittenPredicate {
  body: unknown;
  /** Node id → the JSON path survey-api answers a problem about it at (`predicate.all[1]`). */
  paths: ReadonlyMap<string, string>;
}

/**
 * The builder's tree as the body's `predicate` (§3.4) — composed, never refused. An aggregate's
 * count goes as a JSON number where it is plain decimal text and as typed where not, for
 * survey-api to answer at `predicate.gte`.
 */
export function writePredicate(kind: ConcernCriterionKind, tree: IPredicateTree): IWrittenPredicate {
  const paths = new Map<string, string>();
  if (kind !== 'aggregate') return { body: writeNode(tree.root, 'predicate', paths), paths };
  const { atLeast } = tree;
  const gte = typeof atLeast === 'string' ? (plainNumber(atLeast) ?? atLeast) : atLeast;
  return { body: { count: writeNode(tree.root, 'predicate.count', paths), gte }, paths };
}

/** A condition in words. `value` is `null` where the operator takes none (`blank`). */
export interface IRuleLeafWords {
  id: string;
  type: 'leaf';
  field: string;
  operator: string;
  value: Nullable<string>;
  /** No field, no operator, or no value yet — the form's "(a condition not filled in yet)". */
  unfinished: boolean;
}

export interface IRuleGroupWords {
  id: string;
  type: 'group';
  op: 'all' | 'any';
  items: readonly (IRuleLeafWords | IRuleGroupWords)[];
}

/** A rule in words: who it raises an item for, and its conditions under the group's word. */
export interface IRuleWords {
  /** `null` for "each disclosure"; the count's text for "a person with at least n". */
  atLeast: Nullable<string>;
  group: IRuleGroupWords;
}

function valueWords(leaf: IPredicateLeaf, type: PredicateMemberType | undefined): string {
  const { value } = leaf;
  // A value holding a comma is quoted, or "fraud, theft" would read as two values.
  if (Array.isArray(value)) return value.map((one) => (one.includes(',') ? `“${one}”` : one)).join(', ');
  if (type === 'number') {
    const amount = typeof value === 'number' ? value : plainNumber(String(value));
    if (amount === null) return String(value);
    // survey-api compares the number exactly, so its words never round it: dollars and cents
    // where it has no more, every digit it has where it does.
    const whole = { maximumFractionDigits: 20 };
    if (disclosureField(leaf.member)?.kind !== 'money') return amount.toLocaleString('en-US', whole);
    if (Math.round(amount * 100) / 100 === amount) return formatMoney(amount);
    return `${amount < 0 ? '-' : ''}$${Math.abs(amount).toLocaleString('en-US', whole)}`;
  }
  return String(value);
}

function leafWords(leaf: IPredicateLeaf): IRuleLeafWords {
  const type = predicateMemberType(leaf.member);
  const field = disclosureField(leaf.member)?.label ?? leaf.member;
  const operator = (type && OP_WORDS[type][leaf.op as PredicateOp]) ?? leaf.op;
  const blank = leaf.op === 'blank';
  const empty =
    !blank &&
    (leaf.value === '' || (Array.isArray(leaf.value) && leaf.value.length === 0));
  return {
    id: leaf.id,
    type: 'leaf',
    field,
    operator,
    value: blank ? null : valueWords(leaf, type),
    unfinished: leaf.member === '' || leaf.op === '' || empty
  };
}

function groupWords(group: IPredicateGroup): IRuleGroupWords {
  return {
    id: group.id,
    type: 'group',
    op: group.op,
    items: group.items.map((item) => (item.type === 'group' ? groupWords(item) : leafWords(item)))
  };
}

/**
 * A rule in words, for `RuleWords` to draw — the reading view's and the form's sentence alike.
 * Words, not markup: the renderer decides what is bold. A field, operator or count this module
 * has no words for is said as spelled; nothing is dropped.
 */
export function ruleWords(kind: ConcernCriterionKind, tree: IPredicateTree): IRuleWords {
  return {
    atLeast: kind === 'aggregate' ? String(tree.atLeast ?? '') : null,
    group: groupWords(tree.root)
  };
}

/** An empty value for an operator: `true` for `blank`, an empty list for a list, else `''`. */
export function emptyValue(op: string): PredicateValue {
  if (op === 'blank') return true;
  return isListOp(op) ? [] : '';
}

/**
 * The values the criteria the page holds compare each Text member to (`eq` and `in`), distinct,
 * in the order the versions list them — the builder's "Used by other criteria" (the design's
 * §3.3). Read off the page's own list, never a vocabulary: survey-api keeps none of FINRA's
 * disclosure types either, and the box stays free text. A predicate the words cannot read
 * offers nothing.
 */
export function usedValues(
  versions: readonly { kind: ConcernCriterionKind; predicate: unknown }[]
): ReadonlyMap<string, readonly string[]> {
  const used = new Map<string, Set<string>>();
  const visit = (node: PredicateNode) => {
    if (node.type === 'group') {
      node.items.forEach(visit);
      return;
    }
    if (predicateMemberType(node.member) !== 'text') return;
    const values = Array.isArray(node.value) ? node.value : [node.value];
    for (const value of values) {
      if (typeof value !== 'string') continue;
      const held = used.get(node.member) ?? new Set<string>();
      held.add(value);
      used.set(node.member, held);
    }
  };
  for (const version of versions) {
    const tree = readPredicate(version.kind, version.predicate);
    if (tree) visit(tree.root);
  }
  return new Map([...used].map(([member, values]) => [member, [...values]]));
}

/**
 * A code from a criterion's name, as the form proposes it (the design's §3.3): lowercase, every
 * run of anything but a letter or a digit one `_`, trimmed of `_`, cut at 64. A convenience, not a
 * rule — survey-api judges the code (`^[a-z0-9_]{1,64}$`), and a name with no letter or digit in
 * it derives `''`, which it refuses at `code` like any other.
 */
export function deriveCode(name: string): string {
  const code = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return code.slice(0, 64).replace(/_+$/, '');
}

/** A problem survey-api answered about the predicate — `target` its path, `reason` its words. */
export interface IPredicateProblem {
  target: string;
  reason: string;
}

/** The predicate's problems laid out on the tree: by node id, and those no node carries. */
export interface IPlacedProblems {
  byNode: ReadonlyMap<string, readonly string[]>;
  unplaced: readonly IPredicateProblem[];
}

/**
 * Each problem on the node whose path it names (the design's §5): a condition carries its own path
 * and `.member`, `.op` and `.value` under it; a group its own path and its array's (`.all`,
 * `.any`). Matched on survey-api's spelling alone — Task 2's probe found every path rewritten from
 * `$` to `predicate`. A path no node carries (a parser grown past this mirror, an aggregate's
 * `predicate.gte`) is left for the caller to say whole — never dropped, never guessed onto a row.
 */
export function placeProblems(
  problems: readonly IPredicateProblem[],
  tree: IPredicateTree,
  paths: ReadonlyMap<string, string>
): IPlacedProblems {
  const owners = new Map<string, string>();
  const visit = (node: PredicateNode) => {
    const path = paths.get(node.id);
    if (path === undefined) return;
    owners.set(path, node.id);
    if (node.type === 'group') {
      owners.set(`${path}.${node.op}`, node.id);
      node.items.forEach(visit);
    } else {
      for (const key of ['member', 'op', 'value']) owners.set(`${path}.${key}`, node.id);
    }
  };
  visit(tree.root);
  const byNode = new Map<string, string[]>();
  const unplaced: IPredicateProblem[] = [];
  for (const problem of problems) {
    const owner = owners.get(problem.target);
    if (owner === undefined) unplaced.push(problem);
    else byNode.set(owner, [...(byNode.get(owner) ?? []), problem.reason]);
  }
  return { byNode, unplaced };
}

/**
 * The placed problems once the conditions moved under them. Problems are placed once, against the
 * paths of the body survey-api refused, and follow their node from then on: a removed condition
 * takes its problems with it, and one whose field or operator changed drops them — they were said
 * of another question. A path names a position, so placing afresh on every edit would move a
 * refusal onto whichever condition took the place.
 */
export function forgetMoved(
  placed: IPlacedProblems,
  before: IPredicateGroup,
  after: IPredicateGroup
): IPlacedProblems {
  const leavesOf = (group: IPredicateGroup, into = new Map<string, IPredicateLeaf>()) => {
    for (const item of group.items) {
      if (item.type === 'group') leavesOf(item, into);
      else into.set(item.id, item);
    }
    return into;
  };
  const was = leavesOf(before);
  const now = leavesOf(after);
  const byNode = new Map(
    [...placed.byNode].filter(([id]) => {
      const old = was.get(id);
      const current = now.get(id);
      return !old || (current !== undefined && current.member === old.member && current.op === old.op);
    })
  );
  return { byNode, unplaced: placed.unplaced };
}
